const PBKDF2_ITERATIONS = 100_000;
const PBKDF2_KEY_BITS = 256;
const SALT_PREFIX = 'clinvea:';

export const PASSWORD_HASH_HEADER = 'X-Password-Format';
export const PASSWORD_HASH_VALUE = `pbkdf2-sha256-${PBKDF2_ITERATIONS}`;

/**
 * Derives a PBKDF2-SHA256 hash of the password using the email as a domain-scoped salt.
 * 100k iterations makes brute-force expensive even if the hash leaks over the wire.
 */
export async function hashPasswordForTransmission(
  password: string,
  email: string,
): Promise<string> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const salt = enc.encode(SALT_PREFIX + email.toLowerCase());
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS },
    keyMaterial,
    PBKDF2_KEY_BITS,
  );
  return Array.from(new Uint8Array(bits))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Client-side rate limiter with exponential backoff.
 * Keeps state in memory — resets on page reload (intentional).
 */
export class LoginRateLimiter {
  private attempts = 0;
  private lockedUntil: number | null = null;
  private readonly maxAttempts: number;
  private readonly baseLockMs: number;

  constructor(maxAttempts = 5, baseLockMs = 30_000) {
    this.maxAttempts = maxAttempts;
    this.baseLockMs = baseLockMs;
  }

  isLocked(): boolean {
    if (this.lockedUntil === null) return false;
    if (Date.now() < this.lockedUntil) return true;
    this.lockedUntil = null;
    return false;
  }

  secondsRemaining(): number {
    if (!this.lockedUntil) return 0;
    return Math.max(0, Math.ceil((this.lockedUntil - Date.now()) / 1000));
  }

  recordFailure(): void {
    this.attempts++;
    if (this.attempts >= this.maxAttempts) {
      const extra = this.attempts - this.maxAttempts;
      const delay = this.baseLockMs * Math.pow(2, extra);
      this.lockedUntil = Date.now() + Math.min(delay, 15 * 60_000); // cap at 15 min
    }
  }

  recordSuccess(): void {
    this.attempts = 0;
    this.lockedUntil = null;
  }

  /** Override lock with a server-authoritative duration (e.g. from Retry-After). */
  forceLockFor(seconds: number): void {
    this.lockedUntil = Date.now() + seconds * 1000;
    this.attempts = Math.max(this.attempts, this.maxAttempts);
  }

  attemptsUsed(): number {
    return this.attempts;
  }

  attemptsRemaining(): number {
    return Math.max(0, this.maxAttempts - this.attempts);
  }
}

export function normalizeCredentials(email: string, password: string) {
  return { email: email.trim().toLowerCase(), password: password.trim() };
}
